import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';

interface QueryInputProps {
  onRunQuery: (query: string, isPreview: boolean) => void;
  disabled?: boolean;
  tableName?: string;
  externalQuery?: string;
}

const QueryInput: React.FC<QueryInputProps> = ({ 
  onRunQuery, 
  disabled = false, 
  tableName = 'data_table',
  externalQuery,
}) => {
  const [query, setQuery] = useState(`SELECT * FROM ${tableName} LIMIT 10;`);

  useEffect(() => {
    if (externalQuery) {
      setQuery(externalQuery);
    }
  }, [externalQuery]);

  const handleRunQuery = () => {
    if (query.trim()) {
      onRunQuery(query, false);
    }
  };

  const handlePreviewQuery = () => {
    if (query.trim()) {
      // Add LIMIT 10 to the query for preview if not already present
      const previewQuery = query.toLowerCase().includes('limit') 
        ? query 
        : `${query.replace(/;?\s*$/, '')} LIMIT 10;`;
      onRunQuery(previewQuery, true);
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    setQuery(value || '');
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6">
      <div className="neon-panel p-6">
        <div className="neon-panel-header">
          SQL Terminal Interface
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 font-mono text-sm">
          <div className="text-center">
            <div className="text-gray-400 mb-2">Target Table:</div>
            <div className="neon-glow-text text-lg">{tableName}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Engine:</div>
            <div className="text-neon-blue text-lg">DuckDB-WASM</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Status:</div>
            <div className={`text-lg ${disabled ? 'text-red-400' : 'text-neon-green'}`}>
              {disabled ? '[OFFLINE]' : '[ONLINE]'}
            </div>
          </div>
        </div>
      </div>

      <div className="neon-panel">
        <div className="neon-panel-header">
          Query Editor
        </div>
        <div className="p-6">
          <Editor
            height="200px"
            defaultLanguage="sql"
            value={query}
            onChange={handleEditorChange}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 16,
              fontFamily: "'VT323', monospace",
              lineNumbers: 'on',
              glyphMargin: false,
              folding: false,
              lineDecorationsWidth: 0,
              lineNumbersMinChars: 3,
              renderLineHighlight: 'all',
              selectOnLineNumbers: true,
              wordWrap: 'on',
              automaticLayout: true,
              readOnly: disabled,
              cursorStyle: 'block',
              cursorBlinking: 'solid',
            }}
          />
        </div>
      </div>

      <div className="flex gap-6 justify-center">
        <button
          onClick={handlePreviewQuery}
          disabled={disabled || !query.trim()}
          className={`neon-button neon-button-blue text-lg px-8 py-4 ${
            disabled || !query.trim() ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Preview Query
        </button>
        <button
          onClick={handleRunQuery}
          disabled={disabled || !query.trim()}
          className={`neon-button text-lg px-8 py-4 ${
            disabled || !query.trim() ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Execute Query
        </button>
      </div>

      <div className="neon-panel p-6">
        <div className="neon-panel-header">
          Quick Commands
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-sm max-w-4xl mx-auto">
          <div 
            className="neon-input p-4 cursor-pointer hover:bg-neon-purple hover:bg-opacity-10 transition-colors text-center"
            onClick={() => setQuery(`SELECT * FROM ${tableName} LIMIT 10;`)}
          >
            &gt; SELECT ALL (PREVIEW)
          </div>
          <div 
            className="neon-input p-4 cursor-pointer hover:bg-neon-purple hover:bg-opacity-10 transition-colors text-center"
            onClick={() => setQuery(`SELECT COUNT(*) as total_rows FROM ${tableName};`)}
          >
            &gt; COUNT ROWS
          </div>
          <div 
            className="neon-input p-4 cursor-pointer hover:bg-neon-purple hover:bg-opacity-10 transition-colors text-center"
            onClick={() => setQuery(`DESCRIBE ${tableName};`)}
          >
            &gt; DESCRIBE TABLE
          </div>
          <div 
            className="neon-input p-4 cursor-pointer hover:bg-neon-purple hover:bg-opacity-10 transition-colors text-center"
            onClick={() => setQuery(`SELECT DISTINCT column_name FROM ${tableName};`)}
          >
            &gt; LIST COLUMNS
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryInput; 